import { useState, useEffect } from 'react';

import { createFilmoscope, createSDKPlayerBuilder } from '@kinopoisk-int/filmoscope';

import { FilmoscopeAdapter } from '@/scenarios/legacy-player/lib/filmoscope/filmoscopeAdapter';

import { PlayerAdapter, PlayerApiError, PlayerErrors } from '../../types/instance';
import { PlayerConfig } from '../../types/config';
import {
    PlayerErrorCodeMap,
    PlayerErrorMessageMap,
    PlayerLoadingErrorCategory,
} from '../../constants/constants';

type Params = {
    config: PlayerConfig;
    useYasp?: boolean;
    useSDKConfig?: boolean;
    isDebugMode?: boolean;
    onLoadingError?: (error: PlayerApiError) => void;
};

function createLoadingError(error: unknown) {
    return {
        message:
            error instanceof Error
                ? error.message
                : PlayerErrorMessageMap[PlayerErrors.loadingPlayerError],
        code: PlayerErrorCodeMap[PlayerErrors.loadingPlayerError],
        details: '',
        isFatal: true,
        isDrmError: false,
        category: PlayerLoadingErrorCategory.Network,
    };
}

export function useFilmoscope({ config, useYasp, useSDKConfig, onLoadingError }: Params) {
    const [instance, setInstance] = useState<PlayerAdapter | null>(null);

    const {
        testIds,
        debug,
        autoplay,
        maxABRHeight,
        maxABRWidth,
        serviceId,
        yaspVersion,
        staticPath,
        filmoscopeVersion,
        filmoscopeCheckSafeVersion,
        from,
        playerTelemetryUrl,
        playerPerfUrl,
        featureFlags,
        filmoscopeBaseStaticPath,
        filmoscopeDisableYaspLoader,
        filmoscopeYaspScriptUrl,
    } = config;

    useEffect(() => {
        if (!instance) {
            if (useSDKConfig && config.configType === 'graphQLRequester') {
                const player = createSDKPlayerBuilder()
                    .registerContentConfig(config.graphQLRequester)
                    .debug(debug)
                    .telemetry({
                        logUrl: playerTelemetryUrl,
                        perfLogUrl: playerPerfUrl,
                        testIds,
                        from,
                    })
                    .playback({
                        restrictABRToElementSize: true,
                        maxABRHeight,
                        maxABRWidth,
                    })
                    .yasp({
                        version: yaspVersion,
                        scriptUrl: filmoscopeYaspScriptUrl,
                        disableYaspLoader: filmoscopeDisableYaspLoader,
                    })
                    .load({
                        baseStaticPath: filmoscopeBaseStaticPath,
                        staticPath,
                        checkSafeVersion: filmoscopeCheckSafeVersion,
                        onError: (error) => {
                            onLoadingError?.(createLoadingError(error));
                        },
                    })
                    .useEngineName(useYasp ? 'yasp' : '')
                    .version(filmoscopeVersion)
                    .featureFlags(featureFlags)
                    .build();

                const playback = player.createLegacyPlayback({});

                if (debug && typeof window !== 'undefined') {
                    window.playerInstance = playback;
                }

                if (debug) {
                    playback.showDebugPanel();
                }

                const adapter = new FilmoscopeAdapter(playback, { useSDKConfig });
                adapter.autoplayPolicy = autoplay;
                setInstance(adapter);
            } else {
                const player = createFilmoscope({
                    playerParams: {
                        telemetryParams: {
                            logUrl: playerTelemetryUrl,
                            perfLogUrl: playerPerfUrl,
                            testIds,
                            from,
                        },
                        restrictABRToElementSize: true,
                        useEngineName: useYasp ? 'yasp' : '',
                        yaspVersion,
                        debug,
                        maxABRHeight,
                        maxABRWidth,
                        featureFlags,
                        baseStaticPath: filmoscopeBaseStaticPath,
                        disableYaspLoader: filmoscopeDisableYaspLoader,
                        yaspScriptUrl: filmoscopeYaspScriptUrl,
                    },
                    loaderParams: {
                        staticPath,
                        version: filmoscopeVersion,
                        checkSafeVersion: filmoscopeCheckSafeVersion,
                        onError: (error) => {
                            onLoadingError?.(createLoadingError(error));
                        },
                    },
                });

                if (debug && typeof window !== 'undefined') {
                    window.playerInstance = player;
                }

                if (debug) {
                    player.showDebugPanel();
                }

                const adapter = new FilmoscopeAdapter(player, { useSDKConfig: false });
                adapter.autoplayPolicy = autoplay;

                setInstance(adapter);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        instance,
        testIds,
        useYasp,
        yaspVersion,
        debug,
        serviceId,
        maxABRHeight,
        maxABRWidth,
        staticPath,
        filmoscopeVersion,
        filmoscopeCheckSafeVersion,
        autoplay,
        from,
        onLoadingError,
        playerTelemetryUrl,
        playerPerfUrl,
        featureFlags,
        filmoscopeBaseStaticPath,
        filmoscopeDisableYaspLoader,
        filmoscopeYaspScriptUrl,
        useSDKConfig,
        config.configType,
    ]);

    useEffect(
        () => () => {
            if (instance) {
                instance.destroy();
            }
        },
        [instance]
    );

    return instance;
}
